import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { Film, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';

const PILIER_COLORS: Record<string, string> = {
  paja: 'bg-yellow-100 text-yellow-900 border-yellow-200',
  livre: 'bg-blue-100 text-blue-900 border-blue-200',
  ecole: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  coulisses: 'bg-purple-100 text-purple-900 border-purple-200',
  guide: 'bg-rose-100 text-rose-900 border-rose-200',
};

const PILIER_LABELS: Record<string, string> = {
  paja: 'PAJA — livret',
  livre: 'LIVRE — featured',
  ecole: 'ECOLE — B2B',
  coulisses: 'COULISSES — nurture',
  guide: 'GUIDE — premium',
};

export function ContentPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const scriptsQuery = useQuery({
    queryKey: ['content-scripts', slug],
    queryFn: () => api.listContentScripts(slug!, () => getToken()),
    refetchInterval: 60_000,
  });

  const grouped = scriptsQuery.data?.reduce<Record<string, NonNullable<typeof scriptsQuery.data>>>((acc, s) => {
    if (!acc[s.pilier]) acc[s.pilier] = [];
    acc[s.pilier]!.push(s);
    return acc;
  }, {});

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Pipeline contenus</h1>
        <p className="text-sm text-ducens-muted mt-1">
          50 scripts répartis sur 5 piliers éditoriaux. Statut de production et publication.
        </p>
      </div>

      {scriptsQuery.isPending && (
        <div className="flex items-center gap-2 text-ducens-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      )}

      {scriptsQuery.data && scriptsQuery.data.length === 0 && (
        <Empty
          Icon={Film}
          title="Aucun script encore"
          description="Le pipeline est vide. Lancez la session de seed des 50 scripts pour démarrer."
        />
      )}

      {grouped && (
        <div className="space-y-6">
          {Object.entries(grouped).map(([pilier, scripts]) => (
            <div key={pilier}>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={cn(
                    'inline-flex px-2 py-1 rounded-md text-xs font-medium border',
                    PILIER_COLORS[pilier] ?? 'bg-gray-100 text-gray-900 border-gray-200'
                  )}
                >
                  {PILIER_LABELS[pilier] ?? pilier}
                </span>
                <span className="text-sm text-ducens-muted">{scripts.length} scripts</span>
              </div>
              <div className="space-y-2">
                {scripts.map((script) => (
                  <ScriptRow key={script.id} script={script} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

type Script = Awaited<ReturnType<typeof api.listContentScripts>>[number];

function ScriptRow({ script }: { script: Script }) {
  return (
    <div className="bg-white border border-ducens-border rounded-md px-4 py-3 flex items-center gap-4 text-sm">
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{script.title}</div>
        <div className="text-xs text-ducens-muted truncate mt-0.5">{script.hook}</div>
      </div>
      <div className="hidden md:flex items-center gap-3 text-xs text-ducens-muted shrink-0">
        <span>{script.duration_s}s</span>
        <StatusPill
          label="Variantes"
          ok={script.has_variants}
          okText="8 plateformes"
          koText="à produire"
        />
        <span>{script.scheduled_count} programmées · {script.published_count} publiées</span>
      </div>
      <span
        className={cn(
          'shrink-0 inline-flex px-2 py-0.5 rounded-md text-xs font-medium',
          script.status === 'approved' && 'bg-emerald-100 text-emerald-900',
          script.status === 'draft' && 'bg-amber-100 text-amber-900',
          script.status === 'archived' && 'bg-gray-100 text-gray-900'
        )}
      >
        {script.status}
      </span>
    </div>
  );
}

function StatusPill({ label, ok, okText, koText }: { label: string; ok: boolean; okText: string; koText: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1', ok ? 'text-ducens-success' : 'text-ducens-muted')}>
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full',
          ok ? 'bg-ducens-success' : 'bg-ducens-muted/50'
        )}
      />
      <span>{label} : {ok ? okText : koText}</span>
    </span>
  );
}
